import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Share2, 
  Twitter, 
  Linkedin, 
  Facebook, 
  Copy, 
  CheckCircle2,
  ExternalLink,
  TrendingUp,
  AlertTriangle,
  Zap
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SharedReportPage() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (shareId) {
      fetchReport();
    }
  }, [shareId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/share/${shareId}`);
      if (response.data.success) {
        setReport(response.data.report);
      }
    } catch (error) {
      toast.error("Report not found");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const trackShare = async (platform) => {
    try {
      await axios.post(`${BACKEND_URL}/api/share/${shareId}/track-share?platform=${platform}`);
    } catch (e) {}
  };

  const shareOnTwitter = () => {
    trackShare("twitter");
    window.open(
      `https://twitter.com/intent/tweet?text=My%20website%20scored%20${report?.score}/100%20on%20SEO!%20Check%20yours%20free&url=${encodeURIComponent(window.location.href)}`,
      "_blank"
    );
  };

  const shareOnLinkedIn = () => {
    trackShare("linkedin");
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
      "_blank"
    );
  };

  const shareOnFacebook = () => {
    trackShare("facebook");
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
      "_blank"
    );
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "from-green-500 to-emerald-600";
    if (score >= 60) return "from-yellow-500 to-amber-600";
    if (score >= 40) return "from-orange-500 to-orange-600";
    return "from-red-500 to-red-600";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Report Not Found</h2>
          <p className="text-gray-600 mb-4">This report may have been removed or the link is invalid.</p>
          <Button onClick={() => navigate("/")}>Go to Homepage</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50">
      {/* Header */}
      <div className="bg-white border-b py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <Zap className="w-6 h-6 text-violet-600" />
            <span className="font-bold text-gray-900">MarkeeMix</span>
          </div>
          <Button onClick={() => navigate("/")} variant="outline" size="sm">
            Get Your Free Score
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Score Card */}
        <Card className="overflow-hidden mb-8">
          <div className={`bg-gradient-to-r ${getScoreBg(report.score)} p-8 text-white text-center`}>
            <p className="text-white/80 mb-2">SEO Score for</p>
            <h1 className="text-2xl font-bold mb-6">{report.domain}</h1>
            
            <div className="inline-flex items-center justify-center w-40 h-40 rounded-full bg-white/20 backdrop-blur-sm mb-4">
              <div className="w-32 h-32 rounded-full bg-white flex items-center justify-center">
                <div className="text-center">
                  <span className={`text-5xl font-bold ${getScoreColor(report.score)}`}>{report.score}</span>
                  <span className="text-gray-400 text-lg">/100</span>
                </div>
              </div>
            </div>
            
            <Badge className="bg-white/20 text-white border-0 text-lg px-4 py-1">
              {report.score_status}
            </Badge>
          </div>

          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-600">{report.issues_found}</p>
                <p className="text-sm text-gray-600">Issues Found</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-600">{report.recommendations}</p>
                <p className="text-sm text-gray-600">Recommendations</p>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="border-t pt-6">
              <p className="text-sm text-gray-600 text-center mb-4">Share this report</p>
              <div className="flex justify-center gap-3">
                <Button onClick={shareOnTwitter} variant="outline" size="sm" className="gap-2">
                  <Twitter className="w-4 h-4" /> Twitter
                </Button>
                <Button onClick={shareOnLinkedIn} variant="outline" size="sm" className="gap-2">
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </Button>
                <Button onClick={shareOnFacebook} variant="outline" size="sm" className="gap-2">
                  <Facebook className="w-4 h-4" /> Facebook
                </Button>
                <Button onClick={copyLink} variant="outline" size="sm" className="gap-2">
                  {copied ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied!" : "Copy Link"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Watermark / CTA */}
        <Card className="bg-gradient-to-r from-violet-600 to-purple-600 text-white text-center p-8">
          <h2 className="text-2xl font-bold mb-2">Want to improve your SEO score?</h2>
          <p className="text-violet-200 mb-6">Get a detailed audit with actionable recommendations</p>
          <Button 
            onClick={() => navigate("/")} 
            className="bg-white text-violet-700 hover:bg-gray-100"
          >
            Get Your Free SEO Audit
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-xs text-violet-300 mt-4">
            Powered by MarkeeMix • 75+ Marketing Tools
          </p>
        </Card>

        {/* Stats */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Report created on {new Date(report.created_at).toLocaleDateString()} • 
          Viewed {report.views} times • Shared {report.shares} times
        </p>
      </div>
    </div>
  );
}
